package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.until
import org.jetbrains.compose.web.css.px

/**
 * The send button: what is painted, what can be touched, and the room the field keeps for it.
 *
 * The two sizes are different on purpose, and that is the whole point. At 44px painted, a solid
 * accent disc was the loudest thing in the panel - twice its own arrow and taller than two lines
 * of the field it sits in. But 44 is the minimum touch target and shrinking it would trade a
 * proportion problem for an accessibility one. So the disc is 36 and the hit area is still 44,
 * extended by a transparent `::before`: what you see is the button, what you can hit is bigger.
 */
private val SendButtonSize = 36.px

/** How far the hit area reaches past the disc on each side: 36 + 4 + 4 is the required 44. */
private val SendButtonReach = 4.px



/** The width of the open sessions panel. Shared by the panel and its content. */
private val HistoryWidth = 260.px


/**
 * The application shell: background, color and base type, which everything inherits.
 *
 * Every style in this file reads from [Tokens]: not one color or measurement by hand. Layout
 * goes through `styleModifier` with CSS properties written as-is, because grid and flexbox
 * are the part of Kobweb's API that changes most between versions. And direct children of a
 * Kobweb `Column` get `width: 100%`: a `Column` aligns to the start and each child is as wide
 * as its content, not as the column.
 */
val AppShellStyle = CssStyle.base {
    Modifier
        .backgroundColor(Tokens.bg.value())
        .color(Tokens.fg.value())
        .fontSize(Tokens.textBase.value())
        .styleModifier {
            property("display", "flex")
            property("height", "100vh")
            property("overflow", "hidden")
            property("background-image", Tokens.backdrop.value())
            // The installed app runs full screen with `viewport-fit=cover`, so the page reaches
            // under the front camera and, in landscape, beside it. Zero in a browser tab. The top
            // is not padded here but inside each column - rail, header, toolbar - so the strip
            // under the camera is the surface of whatever is beneath it and not a band of backdrop.
            property(
                "padding",
                "0 env(safe-area-inset-right, 0px) " +
                    "env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px)",
            )
            // What the sessions drawer is positioned against on a narrow screen.
            property("position", "relative")
        }
}

/**
 * The two working columns, to the right of the rail.
 *
 * The artifact takes more room than the chat, which only needs the width of a readable text
 * column. Below [Breakpoints.Md] only one of the two is on screen at a time and the divider
 * drops out of the grid. In landscape the chat width comes from `--chat-width`, which dragging
 * updates without remounting anything, and there is no `column-gap`: the divider itself provides
 * the separation, or the gap between panels would exceed the outer edge.
 */
val WorkspaceStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-rows", "1fr 1fr")
            property("flex", "1 1 auto")
            property("min-width", "0")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding", "${Tokens.spaceMd.value()}")
            property("overflow", "hidden")
        }
    }
    // One pane, edge to edge. The two stacked halves were the phone's whole problem: neither
    // the conversation nor the page had room to be read, so the grid holds a single track and
    // gives up its frame, which is what the panel needs to reach the screen's own edges.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("grid-template-rows", "minmax(0, 1fr)")
            property("gap", "0")
            property("padding", "0")
        }
    }
    Breakpoints.Md {
        Modifier.styleModifier {
            property("grid-template-rows", "none")
            // `minmax` and not a bare track: the floor lives in the grid too, so a stored
            // width from another screen cannot squeeze the column past what fits in it. The
            // `minmax(0, 1fr)` on the page keeps its own content from pushing the split open.
            property(
                "grid-template-columns",
                "minmax(${Layout.ChatMinWidth}px, var(--chat-width, 0.42fr)) auto minmax(0, 1fr)",
            )
            property("column-gap", "0")
        }
    }
}

/**
 * The draggable divider between the chat and the page.
 *
 * It is a control, not a line: it carries `role="separator"`, joins the tab order and
 * responds to the arrow keys. The grabbable area is as wide as the workspace's outer edge,
 * because the divider is the gap between panels, and inside it draws the visible mark.
 */
val ResizerStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "none")
        }
    }
    Breakpoints.Md {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("width", "${Tokens.spaceMd.value()}")
            property("cursor", "col-resize")
            // Without this, dragging the divider selects the chat text next to it.
            property("user-select", "none")
            property("touch-action", "none")
        }
    }
    hover { Modifier.styleModifier { property("background", Tokens.accentWash.value()) } }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "2px solid ${Tokens.accent.value()}")
            property("outline-offset", "-1px")
            property("border-radius", "${Tokens.radiusSm.value()}")
        }
    }
}

/** The visible mark of the divider. */
val ResizerGripStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "2px")
        property("height", "36px")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("background", Tokens.ornament.value())
    }
}

/** Our own breakpoints: Silk's get renamed between versions. */
object Breakpoints {
    val Md = com.varabyte.kobweb.silk.style.breakpoint.Breakpoint.MD

    /**
     * The same threshold as a media query, for the code that has to know the answer in Kotlin.
     *
     * Below [Md] the workspace shows one pane at a time, and hiding the other is not only paint:
     * a control behind `display: none` still answers to the keyboard, so the pane that is off
     * screen has to leave the tab order too, and `inert` is an attribute, not a rule. That
     * decision is taken in Kotlin and has to land on exactly the same pixel as the CSS, which is
     * why this is written as the negation of [Md]'s own query rather than as a value one less.
     */
    const val BelowMd = "not all and (min-width: 48rem)"
}

/**
 * The attribute that marks the pane which is not on screen.
 *
 * It lives here because two places have to agree on it: the suffix rule of each panel style, and
 * [Modifier.offPane], which writes it along with the `inert` that takes the pane out of the tab
 * order. Hiding it with a class of its own would have that class set `display` against the
 * panel's own, and two classes setting the same property tie.
 */
const val OFF_PANE_ATTR = "data-off-pane"

/** The chat column. Its own scroll: the log grows and the artifact must not move. */
val ChatPanelStyle = CssStyle {
    base {
        Modifier.borderRadius(Tokens.radiusLg.value()).glassPanel().fontSize(Tokens.textSm.value()).styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("min-width", "0")
            property("min-height", "0")
        }
    }
    until(Breakpoints.Md) { Modifier.borderRadius(0.px) }
    // A suffix rule on this same class, not a second class: both would be setting `display` and
    // the winner would be whichever Kobweb registered last. The attribute is written by
    // `Modifier.offPane`, which is also what takes the pane out of the tab order.
    cssRule("[$OFF_PANE_ATTR=\"true\"]") { Modifier.styleModifier { property("display", "none") } }
}

/** The chat header: session title and status. */
val ChatHeaderStyle = CssStyle.base {
    Modifier
        .fontSize(Tokens.textSm.value())
        .color(Tokens.muted.value())
        .styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
            property("gap", "${Tokens.spaceMd.value()}")
            property("min-width", "0")
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
            // On a phone the panel reaches the top edge; see `AppShellStyle`.
            property("padding-top", "calc(${Tokens.spaceMd.value()} + $SAFE_AREA_TOP)")
            property("border-bottom", Tokens.glassDivider.value())
            property("flex-shrink", "0")
        }
}

/**
 * The conversation log, with its own scroll.
 *
 * `min-height: 0` lets the flex child shrink below its content and scroll; without it the
 * column overflows and the composer disappears off the bottom. `contain` confines the panel's
 * blur recalculation to this box on every scroll frame, and `overscroll-behavior` keeps the
 * gesture from dragging the page behind once it reaches the end.
 */
val ChatLogStyle = CssStyle.base {
    Modifier.styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
        property("flex", "1 1 auto")
        property("min-height", "0")
        property("overflow-y", "auto")
        property("padding", "${Tokens.spaceXl.value()}")

        property("contain", "layout paint")
        property("overscroll-behavior", "contain")
        property("scroll-behavior", "auto")
    }
}

/**
 * A user turn: a solid accent bubble on the right with a tail at its bottom corner, the shape of a
 * messaging app, so whose words these are is read before a single one of them.
 */
val UserMessageStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.onAccent.value())
            .styleModifier {
                // SpanText emits an inline <span>: without block, neither max-width nor margin auto apply.
                property("display", "block")
                property("position", "relative")
                property("max-width", "85%")
                property("width", "fit-content")
                // The right margin is the tail's room: without it the log's edge cuts the tail off.
                property("margin", "0 $TAIL_REACH ${Tokens.spaceXs.value()} auto")
                property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
                property("background", "${Tokens.accent.value()}")
                property("border-radius", "$BUBBLE_RADIUS $BUBBLE_RADIUS $TAIL_CORNER $BUBBLE_RADIUS")
                property("line-height", Leading.Normal)
                property("white-space", "pre-wrap")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule("::after") { bubbleTail(Tokens.accent.value().toString(), TAIL_RIGHT, side = "right") }
}

/**
 * An agent turn: the same bubble in a quiet neutral on the left, its tail pointing at the avatar.
 * The measure is capped at 68 characters so the eye does not lose the start of the line.
 */
val AgentMessageStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .styleModifier {
                // Inline, neither the measure nor the bottom margin apply.
                property("display", "block")
                property("position", "relative")
                property("width", "fit-content")
                property("max-width", "68ch")
                property("margin", "0 0 ${Tokens.spaceXs.value()} $TAIL_REACH")
                property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
                property("background", "${Tokens.raised.value()}")
                property("border-radius", "$BUBBLE_RADIUS $BUBBLE_RADIUS $BUBBLE_RADIUS $TAIL_CORNER")
                property("line-height", Leading.Relaxed)
                property("white-space", "pre-wrap")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule("::after") { bubbleTail(Tokens.raised.value().toString(), TAIL_LEFT, side = "left") }
}

private const val BUBBLE_RADIUS = "18px"

/**
 * The corner the tail hangs from. Nearly square: a full radius there curves away from the tail
 * and leaves a notch between the two, which reads as a bubble cut short.
 */
private const val TAIL_CORNER = "4px"

/** How far the tail sticks out past the bubble, which is the room left for it beside the bubble. */
private const val TAIL_REACH = "7px"

/**
 * The hook under a bubble's corner, cut from the bubble's own color with a mask.
 *
 * A mask and not the usual second pseudo-element painted in the background color: the log sits on
 * glass, and a patch of solid color would show as a square on it. Half of it lies inside the
 * bubble, which is what squares that corner off so the curve leaves from a straight edge.
 */
private fun bubbleTail(color: String, mask: String, side: String): Modifier = Modifier.styleModifier {
    property("content", "\"\"")
    property("position", "absolute")
    property("bottom", "0")
    property(side, "-$TAIL_REACH")
    property("width", "14px")
    property("height", "20px")
    property("background", color)
    property("mask-image", mask)
    property("-webkit-mask-image", mask)
    property("mask-size", "100% 100%")
    property("-webkit-mask-size", "100% 100%")
}

private const val TAIL_RIGHT = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
    "viewBox='0 0 14 20'%3E%3Cpath d='M0 0H7C7 9 9 15.5 14 20H0Z'/%3E%3C/svg%3E\")"

private const val TAIL_LEFT = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
    "viewBox='0 0 14 20'%3E%3Cpath d='M14 0H7C7 9 5 15.5 0 20H14Z'/%3E%3C/svg%3E\")"

/**
 * Copy, edit and delete under a message, lined up with its bubble. Quiet enough to sit under every
 * turn without competing with the words above.
 */
val MessageActionsStyle = CssStyle.base {
    Modifier.styleModifier {
        // Out of the flow, in the step between turns: hidden, the icons take no room of their own,
        // so the space under a bubble is the same whether or not it is being hovered.
        property("position", "absolute")
        property("left", "0")
        property("bottom", "${Tokens.spaceXs.value()}")
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceXxs.value()}")
    }
}

/**
 * A turn and its actions, so the icons show only while the turn is being pointed at or has focus.
 * It also sets the spacing of everything in it.
 *
 * The icons keep their room while hidden, so they appear without pushing the thread. On a phone
 * a tap on the message is what gives it `:hover`, which is the way in there.
 */
val MessageTurnStyle = CssStyle {
    // One rhythm for the whole thread: the same gap between the pieces of a turn (bubble, card,
    // note) and one larger step between turns, instead of each piece bringing its own. The step
    // is padding and not margin so the icons can sit inside it, still part of the hovered turn.
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding-bottom", "calc(${Tokens.controlSm.value()} + ${Tokens.spaceLg.value()})")
        }
    }
    // Attribute plus class outranks the single class each piece is styled with, so their own
    // vertical margins give way to the gap; the side margins that align the bubbles stay.
    cssRule(" > [class]") {
        Modifier.styleModifier {
            property("margin-top", "0")
            property("margin-bottom", "0")
        }
    }
    // Entrance of a turn that arrives live, from the side it belongs to. The reduced-motion rule
    // in `AppEntry` cancels it for whoever asked their system for less movement.
    cssRule("[data-enter]") {
        Modifier.styleModifier {
            property("animation", "ha-bubble-in ${Motion.Slow} ${Motion.Curve} both")
        }
    }
    cssRule("[data-enter=\"end\"]") { Modifier.styleModifier { property("transform-origin", "bottom right") } }
    cssRule("[data-enter=\"start\"]") { Modifier.styleModifier { property("transform-origin", "bottom left") } }
    cssRule(" [data-turn-icons]") {
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "${Tokens.spaceXxs.value()}")
            property("opacity", "0")
            property("transition", "opacity ${Motion.Fast} ${Motion.Ease}")
        }
    }
    cssRule(":hover [data-turn-icons]") { Modifier.styleModifier { property("opacity", "1") } }
    cssRule(":focus-within [data-turn-icons]") { Modifier.styleModifier { property("opacity", "1") } }
}

/** The user's actions sit under the right edge, where their bubble ends. */
val MessageActionsEndStyle = CssStyle.base {
    Modifier.styleModifier {
        property("left", "auto")
        property("right", TAIL_REACH)
    }
}

/**
 * An icon-only action under a message. Its name comes from the tooltip and the `aria-label`, never
 * from guessing at the glyph.
 */
val MessageActionVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("display", "inline-grid")
                property("place-items", "center")
                property("width", "${Tokens.controlSm.value()}")
                property("height", "${Tokens.controlSm.value()}")
                property("min-height", "${Tokens.controlSm.value()}")
                property("padding", "0")
                property("background", "transparent")
                property("border", "none")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/**
 * The message being rewritten: the composer's box, so editing looks like writing. The ring goes
 * on the box and not on the field inside it, which is bare; one border, not two.
 */
val MessageEditBoxStyle = CssStyle {
    base {
        Modifier.borderRadius(Tokens.radiusMd.value()).glassWell().styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceMd.value()}")
            property("width", "100%")
            property("box-sizing", "border-box")
            property("padding", "${Tokens.spaceMd.value()}")
            property("border", "1px solid ${Tokens.lineStrong.value()}")
        }
    }
    cssRule(":focus-within") {
        Modifier.styleModifier {
            property("border-color", "${Tokens.accent.value()}")
            property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}")
        }
    }
}

/**
 * The field inside [MessageEditBoxStyle]. It grows with the text from three lines up to a
 * ceiling, past which it scrolls; a message long enough to need more is edited with the page
 * still in view.
 */
val MessageEditFieldStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .styleModifier {
            property("display", "block")
            property("width", "100%")
            property("box-sizing", "border-box")
            property("min-height", "4.5em")
            property("max-height", "320px")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("outline", "none")
            property("resize", "none")
            property("overflow-y", "auto")
            property("font", "inherit")
            property("line-height", Leading.Normal)
        }
}

/** Cancel and save, at the bottom right of the box, never squeezed by anything beside them. */
val MessageEditBarStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("justify-content", "flex-end")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** What saving does, under the box and read left to right like the rest of the thread. */
val MessageEditHintStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier { property("line-height", Leading.Snug) }
}

/** The shape both edit buttons share: one height, text centred, and room that never shrinks. */
private fun Modifier.editButton(): Modifier = fontSize(Tokens.textXs.value())
    .borderRadius(Tokens.radiusSm.value())
    .styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("flex-shrink", "0")
        property("height", "${Tokens.controlSm.value()}")
        property("min-height", "${Tokens.controlSm.value()}")
        property("padding", "0 ${Tokens.spaceLg.value()}")
        property("font-weight", "${Weight.Semibold}")
        property("white-space", "nowrap")
        property("line-height", "1")
    }

/** Leave the message as it was: quiet, so the eye lands on saving. */
val MessageCancelVariant = ButtonStyle.addVariant {
    base {
        Modifier.editButton().color(Tokens.muted.value()).styleModifier {
            property("background", "transparent")
            property("border", Tokens.glassEdge.value())
        }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/**
 * Where the delete question sits: under the person's bubble, on its side and no wider than a
 * session row, instead of the full width the history panel gives it.
 */
val MessageConfirmStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "min(100%, 300px)")
        property("margin", "${Tokens.spaceXs.value()} 0 ${Tokens.spaceXl.value()} auto")
    }
}

/** Keep the rewrite: filled in the accent, so it reads as the way forward next to cancel. */
val MessageSaveVariant = ButtonStyle.addVariant {
    base {
        Modifier.editButton()
            .backgroundColor(Tokens.accent.value())
            .color(Tokens.onAccent.value())
            .styleModifier { property("border", "none") }
    }
    hover { Modifier.backgroundColor(Tokens.accentHover.value()) }
    // Nothing new to save yet: faded in both themes, where the dark accent alone barely changed.
    cssRule(":disabled") {
        Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
            property("opacity", "0.45")
            property("cursor", "not-allowed")
        }
    }
}

/**
 * What the agent is doing right now: a sheen running across the letters, the only thing
 * moving during a long wait and what tells "working" apart from "hung".
 *
 * The letters rest in `muted`, so the phrase never drops below legible, not even with reduced
 * motion. The sheen is a second copy of them ([ActivitySheenStyle]) and moves with `transform`,
 * not with a background position: that one repainted on the main thread every frame, which is
 * the thread a streaming turn keeps busy, and under 25ms of work per frame it fell to 30fps.
 * No margin of its own: one here would offset the dot from the letters.
 */
val ActivityStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textSm.value())
        .styleModifier {
            property("position", "relative")
            property("overflow", "hidden")
        }
}

/**
 * The window the crest shows through, travelling across the phrase.
 *
 * Its soft edges are a mask, which travels with it; the copy inside counter-travels
 * ([ActivitySheenTextStyle]) so the letters stay put while the light crosses them.
 */
val ActivitySheenStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("inset", "0")
        property("overflow", "hidden")
        property("pointer-events", "none")
        property("mask-image", SHEEN_MASK)
        property("-webkit-mask-image", SHEEN_MASK)
        property("animation", "ha-sheen ${Motion.SheenCycle} ${Motion.Linear} infinite")
    }
}

/**
 * The copy of the phrase in the crest's colors. The accent carries the crest because `muted` and
 * `fg` are adjacent steps and a sweep between them is invisible.
 */
val ActivitySheenTextStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("inset", "0")
        property("white-space", "inherit")
        property(
            "background",
            "linear-gradient(90deg, ${Tokens.accent.value()}, ${Tokens.fg.value()}, ${Tokens.accent.value()})",
        )
        property("background-clip", "text")
        property("-webkit-background-clip", "text")
        property("color", "transparent")
        property("-webkit-text-fill-color", "transparent")
        property("animation", "ha-sheen-counter ${Motion.SheenCycle} ${Motion.Linear} infinite")
    }
}

/** A crest a third of the phrase wide, fading at both sides. */
private const val SHEEN_MASK =
    "linear-gradient(90deg, transparent 25%, #000 45%, #000 55%, transparent 75%)"

/**
 * The icon next to the activity text, which says at a glance what the agent is doing. It sits
 * outside [ActivityStyle] because that one's `color: transparent` would erase an icon painted
 * with `currentColor`, and it breathes as the dot it replaced did, so it still reads as working.
 */
val ActivityIconStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier {
        property("display", "inline-flex")
        property("flex-shrink", "0")
        property("animation", "ha-pulse ${Motion.PulseCycleShort} ${Motion.Breathe} infinite alternate")
    }
}

/** The chat footer, with the composer field. */
val ComposerStyle = CssStyle.base {
    Modifier.styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
        property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceXl.value()}")
        property("border-top", Tokens.glassDivider.value())
        property("flex-shrink", "0")
    }
}

/**
 * The anchor for the send button around the field. No visuals of its own: the field is still
 * the `textarea`, so the focus ring lands on it and not on a box wrapping it.
 */
/**
 * The bordered box that holds the field and its button, as a row rather than a stage.
 *
 * The button used to be `position: absolute` in a corner, with the field reserving padding on
 * its right so the text would not run underneath. That arrangement has to keep three numbers
 * agreeing -the inset, the button, the reserve- and it never quite did: with a two-row field the
 * button hung below the text, and once the field was one row tall it had nowhere to sit and
 * pinned itself to the top edge. As a flex row there is nothing to agree on. The box owns the
 * border, the fill and the focus ring; the field inside is bare.
 */
val ComposerFieldStyle = CssStyle {
    base {
        Modifier.borderRadius(Tokens.radiusMd.value()).glassWell().styleModifier {
            property("display", "flex")
            // The button belongs beside the last line, which is where it stays as the field grows.
            property("align-items", "flex-end")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding", "${Tokens.spaceMd.value()}")
            property("border", "1px solid ${Tokens.lineStrong.value()}")
        }
    }
    // The ring belongs to the box now that the box is what looks like the field.
    cssRule(":focus-within") {
        Modifier.styleModifier {
            property("border-color", "${Tokens.accent.value()}")
            property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}")
        }
    }
}

/**
 * The field where you type.
 *
 * Its border overrides the glass one because a control needs more presence than a card. The
 * ring goes on `:focus-visible` and not on `:focus`: it shows with the keyboard, not on click.
 */
/**
 * The field itself, which is now bare: [ComposerFieldStyle] around it carries everything that
 * looks like a control, so there is no border, fill, radius or padding to keep in step here.
 *
 * No `min-height` either. The row is one line tall because the element says `rows="1"`, and
 * `fitToContent` takes it up from there; a floor would only fight that measurement. The ceiling
 * stays, and past it the field scrolls.
 *
 * It follows the chat's `textSm`, except below [Breakpoints.Md]: iOS Safari zooms the page into
 * any field under 16px when it takes focus.
 */
val ComposerInputStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textSm.value())
            .styleModifier {
                property("display", "block")
                property("flex", "1 1 auto")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("max-height", "200px")
                // The field is as tall as the send button and pads its single line to the middle.
                // Bottom-aligned siblings of different heights are what made the placeholder sit low
                // in the pill: the box was as tall as the 36px button while the text was 26px and
                // hugged the floor. Matched, one line reads centred and the field still grows
                // upwards from there, which is what keeps the button at the bottom.
                property("min-height", SendButtonSize.toString())
                property("padding", "6px 0")
                property("background", "transparent")
                property("border", "none")
                property("line-height", Leading.Normal)
                // A resize handle inside a box that already sizes itself is a second, worse control.
                property("resize", "none")
                property("overflow-y", "auto")
                property("outline", "none")
            }
    }
    until(Breakpoints.Md) { Modifier.fontSize(Tokens.textBase.value()) }
    // One line, cut with an ellipsis: on a phone, with the photo button beside it, the example
    // no longer fits, and a wrapped placeholder made the empty field two lines tall.
    cssRule("::placeholder") {
        Modifier.styleModifier {
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
        }
    }
}

/**
 * Send: a circle inside the field, where the hand already is, and not a button below it.
 * It measures [SendButtonSize] even though the icon is smaller: the touch target is the area,
 * not the drawing.
 */
/**
 * The geometry both composer buttons share: a disc in the field's bottom corner whose hit area
 * is larger than itself.
 */
private fun Modifier.composerAction(): Modifier = styleModifier {
    property("position", "relative")
    property("flex-shrink", "0")
    property("width", "$SendButtonSize")
    property("min-width", "$SendButtonSize")
    property("height", "$SendButtonSize")
    property("border-radius", "50%")
    property("display", "grid")
    property("place-items", "center")
    property("padding", "0")
}

/** The transparent margin that takes the hit area from the painted 36 to the required 44. */
private fun Modifier.composerReach(): Modifier = styleModifier {
    property("content", "\"\"")
    property("position", "absolute")
    property("top", "-$SendButtonReach")
    property("right", "-$SendButtonReach")
    property("bottom", "-$SendButtonReach")
    property("left", "-$SendButtonReach")
    property("border-radius", "50%")
}

val SendButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .backgroundColor(Tokens.accent.value())
            .color(Tokens.onAccent.value())
            .composerAction()
            .styleModifier {
                property("border", "none")
                property("box-shadow", "0 0 0 1px ${Tokens.solidRing.value()}, ${Tokens.glassRaisedShadow.value()}")
            }
    }
    cssRule("::before") { Modifier.composerReach() }
    hover { Modifier.styleModifier { property("filter", "brightness(1.08)") } }

    // Nothing here for the empty-field state: Silk marks a disabled button with `aria-disabled`
    // and its own class, not with the native `disabled`, so a `:disabled` rule never matches. It
    // already dims it, and a second opinion on the same state is how two of them start to drift.
}

/**
 * The send button when its role is to stop: same place, because while generating there is
 * nothing to send. No accent: stopping is not the suggested action, it is the available one.
 */
val StopButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.fg.value()).composerAction().styleModifier {
            property("background", Tokens.glassRaisedFill.value())
            property("border", "1px solid ${Tokens.lineStrong.value()}")
        }
    }
    cssRule("::before") { Modifier.composerReach() }
    hover { Modifier.styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}


/** The artifact column. */
val ArtifactPanelStyle = CssStyle {
    base {
        Modifier.borderRadius(Tokens.radiusLg.value()).glassPanel().styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("min-width", "0")
            property("min-height", "0")
        }
    }
    until(Breakpoints.Md) { Modifier.borderRadius(0.px) }
    // See the same rule on [ChatPanelStyle]: one pane at a time below [Breakpoints.Md].
    cssRule("[$OFF_PANE_ATTR=\"true\"]") { Modifier.styleModifier { property("display", "none") } }
}

/**
 * The page code. It is shown instead of the render while it is being written: a half-finished
 * page looks broken and the user concludes the agent failed.
 */
val ArtifactCodeStyle = CssStyle.base {
    Modifier
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .fontSize(Tokens.textXs.value())
        .color(Tokens.muted.value())
        .styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
            property("flex", "1 1 auto")
            property("min-height", "0")
            property("overflow", "auto")
            property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceXl.value()}")
            property("line-height", Leading.Normal)
            property("white-space", "pre")
            property("margin", "0")
            // A solid background would open an opaque hole in the middle of the glass.
            property("background", Tokens.scrim.value())
        }
}

/**
 * The render iframe. White background, like almost every generated page, and no transition of
 * its own: it takes the target width at once and the window around it is what animates.
 */
val ArtifactFrameStyle = CssStyle.base {
    Modifier.styleModifier {
        property("flex", "0 0 auto")
        property("min-height", "0")
        property("width", "min(100cqw, var($PREVIEW_WIDTH, 100%))")
        property("border", "none")
        property("background", "white")
        property("transition", "none")
    }
}

/** What is shown before the first request. */
val EmptyStateStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
            property("flex", "1 1 auto")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding", "${Tokens.spaceXxxl.value()}")
            property("text-align", "center")
        }
}

/** The first line of an empty state: what this space is. */
val EmptyTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textMd.value()).styleModifier {
        property("font-weight", "${Weight.Semibold}")
    }
}

/** The second: what will happen here, and roughly when. */
val EmptyHintStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("max-width", "42ch")
        property("line-height", Leading.Relaxed)
    }
}

/**
 * The veil over the preview until the artifact origin has actually served the page.
 *
 * Between the turn ending and the iframe painting there were seconds of white inside a frame
 * already labelled ready, which reads as a page that came out blank.
 */
val PreviewVeilStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("background", Tokens.surface.value())
        }
    }
    // It fades only on the way out: a new version has to be covered at once, not faded over.
    cssRule("[data-loaded=\"true\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("visibility", "hidden")
            property("pointer-events", "none")
            property(
                "transition",
                "opacity ${Motion.Base} ${Motion.Ease}, visibility 0s linear ${Motion.Base}",
            )
        }
    }
}

/** Its host: the frame the veil is measured against. */
val PreviewBodyStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("flex", "1 1 auto")
            property("min-height", "0")
            property("display", "flex")
            // The iframe keeps its final width while the window narrows around it: centered, it
            // is cut evenly on both sides instead of from the right.
            property("justify-content", "center")
        }
    }
    // In full screen `cqw` still measures the frame this box left behind.
    cssRule(":fullscreen > iframe") { Modifier.styleModifier { property("width", "100%") } }
}

/**
 * The way out of the full-screen preview, floating in its corner.
 *
 * The bottom-right one: top right is where generated pages put their own menu, and it covered it.
 *
 * In full screen the page fills everything, so the control has to sit on top of it. It carries
 * the dark tokens in both modes: under it is a page of any color, and a dark disc with a light
 * cross reads over all of them. 44px, because on a phone it is the only thing to hit.
 */
val FullscreenCloseStyle = CssStyle.base {
    Modifier.darkTokens().color(Tokens.fg.value()).styleModifier {
        property("position", "absolute")
        property("bottom", "calc(${Tokens.spaceMd.value()} + env(safe-area-inset-bottom, 0px))")
        property("right", "calc(${Tokens.spaceMd.value()} + env(safe-area-inset-right, 0px))")
        property("z-index", "${Layer.Overlay}")
        property("width", "44px")
        property("height", "44px")
        property("display", "grid")
        property("place-items", "center")
        property("padding", "0")
        property("border", Tokens.glassEdge.value())
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("background", Tokens.surface.value())
        property("box-shadow", Tokens.glassPanelShadow.value())
    }
}

/**
 * The preview laid over the whole window instead of going full screen.
 *
 * Used in the installed app, which already runs full screen: asking Android for full screen again
 * and leaving it made the system bars flash on the way out. It is shown as a popover, in the top
 * layer, and these override the browser's popover defaults: centred, content-sized and bordered.
 */
val PreviewOverlayStyle = CssStyle {
    base {
    Modifier.backgroundColor(Tokens.surface.value()).styleModifier {
        property("position", "fixed")
        property("inset", "0")
        property("width", "auto")
        property("height", "auto")
        property("max-width", "none")
        property("max-height", "none")
        property("margin", "0")
        property("padding", "0")
        property("border", "none")
        property("overflow", "hidden")
    }
    }
    // Over the whole window the page is as wide as the window, not as the frame it left.
    cssRule(" > iframe") { Modifier.styleModifier { property("width", "100%") } }
}

/**
 * The page's tools behind one button on a phone: view, versions, link and download.
 *
 * Right-aligned under its button, like [VersionMenuStyle], and wide enough for "Copiar enlace".
 */
val ToolsMenuStyle = CssStyle.base {
    Modifier
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("position", "absolute")
            property("top", "calc(100% + 8px)")
            property("right", "0")
            property("z-index", "${Layer.Popover}")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "stretch")
            property("gap", "${Tokens.spaceMd.value()}")
            property("min-width", "220px")
            property("padding", "${Tokens.spaceMd.value()}")
            property("background", "${Tokens.raised.value()}")
            property("border", Tokens.glassEdge.value())
            property("box-shadow", Tokens.glassRaisedShadow.value())
        }
}

/**
 * An invisible layer under an open menu that closes it when touched anywhere else, the preview
 * included: a tap there lands inside the iframe and never reaches the document's listener.
 */
val MenuDismissStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "fixed")
        property("inset", "0")
        property("z-index", "${Layer.Popover - 1}")
        property("background", "transparent")
    }
}

/** The view switch inside [ToolsMenuStyle]: the two halves share the width it is stretched to. */
val ToolsMenuSegmentsStyle = CssStyle {
    cssRule(" > *") {
        Modifier.styleModifier {
            property("flex", "1 1 0")
            property("justify-content", "center")
        }
    }
}

/** Link and download in [ToolsMenuStyle]: one row each, the same shape, ruled off above. */
val ToolsMenuActionsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "2px")
        property("padding-top", "${Tokens.spaceMd.value()}")
        property("border-top", "1px solid ${Tokens.line.value()}")
    }
}

/**
 * One action row in the menu. The copy button and the download link are a button and an anchor,
 * and in a row they looked like two different things: here they share the segment's quiet shape.
 */
val ToolsMenuActionStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
            property("width", "100%")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "flex-start")
            property("gap", "${Tokens.spaceSm.value()}")
            property("min-height", "${Tokens.controlSm.value()}")
            property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceMd.value()}")
            property("border-radius", "${Tokens.radiusSm.value()}")
            property("text-decoration", "none")
        }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/**
 * The phone's app bar: menu, the Chat | Page switch in the middle, and the pane's tools.
 *
 * Three columns with the outer two equal, so the switch stays centred whether or not the pane has
 * tools on the right. It sits at the top edge, so it pads itself for the camera.
 */
val MobileAppBarStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "100%")
        property("display", "grid")
        property("grid-template-columns", "1fr auto 1fr")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property(
            "padding",
            "calc(${Tokens.spaceSm.value()} + $SAFE_AREA_TOP) ${Tokens.spaceSm.value()} ${Tokens.spaceSm.value()}",
        )
        property("border-bottom", Tokens.glassDivider.value())
        property("flex-shrink", "0")
    }
}

/** The switch's two halves, the same width whatever their word. */
val MobileAppBarSwitchStyle = CssStyle {
    cssRule(" > *") {
        Modifier.styleModifier {
            property("min-width", "88px")
            property("justify-content", "center")
        }
    }
}

/** The right-hand column of [MobileAppBarStyle], pushed to its edge. */
val MobileAppBarEndStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("justify-content", "flex-end")
    }
}

/** The session under the app bar: its name, whether it is working, and what it has cost. */
val MobileSessionLineStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("width", "100%")
        property("min-width", "0")
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceLg.value()}")
        property("border-bottom", Tokens.glassDivider.value())
        property("flex-shrink", "0")
    }
}

/** The session's name in that line: it takes the room and truncates. */
val MobileSessionTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("margin", "0")
        property("flex", "1 1 auto")
        property("min-width", "0")
        property("font-weight", "${Weight.Semibold}")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }
}

/** What dims the app behind the sessions panel, and closes it when touched. */
val DrawerScrimStyle = CssStyle {
    // Always there and faded, so it can fade out with the drawer as well as in.
    base {
        Modifier.styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("z-index", "${Layer.Overlay - 1}")
            property("background", "rgba(4, 8, 18, 0.45)")
            property("opacity", "0")
            property("pointer-events", "none")
            property("transition", "opacity ${Motion.Slow} ${Motion.Ease}")
        }
    }
    // On a desktop the panel covers a column, not the screen: half the dimming says as much.
    Breakpoints.Md {
        Modifier.styleModifier { property("background", "rgba(4, 8, 18, 0.22)") }
    }
    cssRule("[data-open=\"true\"]") {
        Modifier.styleModifier {
            property("opacity", "1")
            property("pointer-events", "auto")
        }
    }
}

/** The two ways to start, side by side. */
val DrawerStartGridStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "100%")
        property("display", "grid")
        property("grid-template-columns", "1fr 1fr")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding-bottom", "${Tokens.spaceMd.value()}")
    }
}

/** One way to start: an icon, its name and what it costs you, as a card that reads as a button. */
val DrawerStartCardStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("width", "100%")
            property("height", "auto")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "${Tokens.spaceXs.value()}")
            property("padding", "${Tokens.spaceMd.value()}")
            property("text-align", "left")
            property("background", Tokens.glassWellFill.value())
            property("border", "1px solid ${Tokens.line.value()}")
            property("border-radius", "${Tokens.radiusMd.value()}")
        }
    }
    // Silk's inner row, which is where the children actually are.
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "${Tokens.spaceXs.value()}")
        }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.accent.value().toString()) } }
}

/** The icon on a start card, tinted with the accent. */
val DrawerStartIconStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier {
        // A flex box, so the glyph inside is one of its items and keeps its size: in an inline
        // span a width and a height do not apply, and the icon measured 0x0.
        property("display", "flex")
        property("margin-bottom", "${Tokens.spaceXs.value()}")
    }
}

/**
 * The account at the foot of the drawer: who, the two switches, and leaving.
 *
 * Pinned to the bottom, which is where a drawer keeps who you are and how the app is set: the
 * actions at the top, the sessions in between. With a long list it stays in view while the
 * sessions scroll behind it.
 */
val DrawerAccountStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.surface.value()).styleModifier {
        property("width", "100%")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceMd.value()}")
        property("margin-top", "auto")
        property("position", "sticky")
        property("bottom", "0")
        property(
            "padding",
            "${Tokens.spaceMd.value()} 0 calc(${Tokens.spaceMd.value()} + env(safe-area-inset-bottom, 0px))",
        )
        property("border-top", "1px solid ${Tokens.line.value()}")
    }
}

/** Who is signed in: an initial in a disc and the address beside it. */
val DrawerWhoStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("min-width", "0")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
}

/** The initial's disc. */
val DrawerAvatarStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).color(Tokens.onAccent.value()).styleModifier {
        property("width", "32px")
        property("height", "32px")
        property("flex-shrink", "0")
        property("display", "grid")
        property("place-items", "center")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("font-weight", "${Weight.Semibold}")
        property("text-transform", "uppercase")
    }
}

/** A label and its two-way switch, on one line. */
val DrawerToggleRowStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** The drawer's head on a phone: the mark and the name, and the way out. */
val DrawerBrandStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "100%")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("padding-bottom", "${Tokens.spaceMd.value()}")
    }
}

/** One setting in the drawer: an icon and what it does, as a row the thumb can hit. */
val DrawerActionStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
            property("width", "100%")
            property("height", "auto")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "flex-start")
            property("gap", "${Tokens.spaceMd.value()}")
            property("min-height", "44px")
            property("padding", "0 ${Tokens.spaceMd.value()}")
            property("background", "transparent")
            property("border", "none")
            property("border-radius", "${Tokens.radiusSm.value()}")
            property("text-align", "left")
        }
    }
    // Silk wraps a button's children in a row of its own, so the gap has to be set there too:
    // on the button alone the icon and its label were touching.
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceMd.value()}")
        }
    }
    hover { Modifier.styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/** A group inside [ToolsMenuStyle]: the versions wrap, and every group after the first is ruled off. */
val ToolsMenuGroupStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "4px")
        property("padding-top", "${Tokens.spaceMd.value()}")
        property("border-top", "1px solid ${Tokens.line.value()}")
    }
}

/** An error notice. Never color alone: always with text explaining what happened. */
val ErrorStyle = CssStyle.base {
    Modifier
        .color(Tokens.danger.value())
        .fontSize(Tokens.textSm.value())
        .styleModifier {
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
            property("border-left", "2px solid ${Tokens.danger.value()}")
            property("margin-bottom", "${Tokens.spaceLg.value()}")
        }
}

/**
 * A turn that ended without a page.
 *
 * It borrows the danger colour for the rule only and keeps the text in the foreground: what
 * happened is recoverable in one click, and red prose would make a retry look like a loss.
 */
val NoticeStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textSm.value())
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("display", "block")
            property("width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
            // Below and not above, like every block in the conversation: with only a top margin
            // the turn's buttons sat against it with no gap at all.
            property("margin-bottom", "${Tokens.spaceXl.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border-left", "2px solid ${Tokens.danger.value()}")
        }
}


/**
 * An action link, like the download one. It keeps the underline: without it color would be
 * the only cue that it is a link.
 */
val DownloadLinkStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.muted.value())
            .fontSize(Tokens.textSm.value())
            .styleModifier {
                property("text-decoration", "underline")
                property("text-underline-offset", "3px")
                // The text is short: the touch area is earned with padding.
                property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceXs.value()}")
                property("border-radius", "${Tokens.radiusSm.value()}")
            }
    }
    hover { Modifier.color(Tokens.fg.value()) }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "2px solid ${Tokens.accent.value()}")
            property("outline-offset", "2px")
        }
    }
}

/**
 * A glass surface: panel or card. It is a function because these are five properties that
 * only work together. `overflow: hidden` keeps scrolling content from spilling out through
 * the rounded corners.
 */
fun Modifier.glassPanel(): Modifier = styleModifier {
    property("background", Tokens.glassPanelFill.value())
    property("backdrop-filter", Glass.Tint)
    // Safari still needs the prefix; without it the panel looks flat there only.
    property("-webkit-backdrop-filter", Glass.Tint)
    property("border", Tokens.glassEdge.value())
    property("box-shadow", Tokens.glassPanelShadow.value())
    property("overflow", "hidden")
}

/**
 * What rests inside a glass panel: bubbles, fields. No blur of its own: the panel already
 * blurs what is behind, and a second `backdrop-filter` would only add cost per item in the
 * list.
 */
fun Modifier.glassRaised(): Modifier = styleModifier {
    property("background", Tokens.glassRaisedFill.value())
    property("border", Tokens.glassEdge.value())
    property("box-shadow", Tokens.glassRaisedShadow.value())
}

/**
 * A well carved into the panel: the composer field. It is [glassRaised] inverted, darkening
 * instead of lightening, and that inversion gives the send button enough contrast.
 */
fun Modifier.glassWell(): Modifier = styleModifier {
    property("background", Tokens.glassWellFill.value())
    property("border", Tokens.glassEdge.value())
    property("box-shadow", Tokens.glassWellShadow.value())
}

/** The icon rail on the left. It only holds controls that do something. */
val RailStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceSm.value()}")
            property("padding-top", "calc(${Tokens.spaceMd.value()} + $SAFE_AREA_TOP)")
            property("flex-shrink", "0")
            property("height", "100%")
            property("border-right", "${Tokens.glassDivider.value()}")
            // Above the sessions panel and its scrim: the panel comes out from behind it, and
            // the rail's buttons still answer while the panel is open.
            property("position", "relative")
            property("z-index", "${Layer.Overlay + 1}")
            property("transition", "background-color ${Motion.Slow} ${Motion.Ease}")
        }
    }
    // With the panel open the rail takes the panel's surface, so the two read as one sheet over
    // the dimmed app. Left see-through, the panel looked laid on top of the rail.
    cssRule("[data-history=\"true\"]") { Modifier.backgroundColor(Tokens.surface.value()) }
    // On a phone it was 58 of 393px on every screen. Its places move into the sessions drawer,
    // opened from the ≡ in the chat header; on a desktop it stays, where it is 4% of the width.
    until(Breakpoints.Md) { Modifier.styleModifier { property("display", "none") } }
}

/**
 * Every piece of the rail keeps its size: with the rail at full height, flexible distribution
 * would stretch the mark and shrink the buttons.
 */
val RailItemStyle = CssStyle.base {
    Modifier.styleModifier {
        property("flex-shrink", "0")
        property("flex-grow", "0")
    }
}

/** The mark. It is not a button: no focus and no pointer cursor. */
val RailMarkStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.markTile.value())
            .borderRadius(Tokens.radiusMd.value())
            .styleModifier {
                property("width", "${Tokens.mark.value()}")
                property("height", "${Tokens.mark.value()}")
                property("display", "grid")
                property("place-items", "center")
                property("margin-bottom", "${Tokens.spaceSm.value()}")
                property("flex-shrink", "0")
            }
    }
    focusVisible { Modifier.focusOutline() }
}

/** A rail button. 40x40 and with a `title`, because an icon alone does not say what it does. */
val RailButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .borderRadius(Tokens.radiusMd.value())
            .styleModifier {
                property("width", "${Tokens.controlMd.value()}")
                property("height", "${Tokens.controlMd.value()}")
                property("display", "grid")
                property("place-items", "center")
                property("background", "transparent")
                property("border", "none")
                property("padding", "0")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/** The active rail button. Background and color, not color alone: tone alone marks no state. */
val RailButtonActiveStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) }
}

/** The session name: the page `<title>` as soon as the model writes it. */
val PanelTitleStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textBase.value())
            .styleModifier {
                // It is an <h1>, and a heading arrives with a margin and a size of its own.
                property("margin", "0")
                // Ellipsis truncation needs a block box that can shrink.
                property("display", "block")
                property("min-width", "0")
                property("flex", "1 1 auto")
                property("font-weight", "${Weight.Semibold}")
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            }
    }
    // Below [Breakpoints.Md] it leaves the row but NOT the document: measured at 381px of header
    // it had 39px left, which is four letters and an ellipsis - it cost the width without naming
    // anything. Taken out of flow rather than `display: none` so it stays the panel's heading for
    // anyone navigating by headings, and because a removed <h1> leaves the chat with none at all.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("position", "absolute")
            property("width", "1px")
            property("height", "1px")
            property("padding", "0")
            property("clip-path", "inset(50%)")
        }
    }
}

/** A status label. The color dot always comes with the text. */
val PillStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("padding", "${Tokens.spaceXs.value()} ${Tokens.spaceSm.value()}")
            property("flex-shrink", "0")
            // With a long title the text used to compress down to just the dot.
            property("white-space", "nowrap")
            property("background", Tokens.glassRaisedFill.value())
            property("border", Tokens.glassEdge.value())
            property("white-space", "nowrap")
        }
}

/**
 * The token half of the consumption label.
 *
 * It disappears below [Breakpoints.Md] and that is the whole fix for a measured overflow: with it,
 * the header needed 409px inside 338px, the title was squeezed to ZERO and the way to the page was
 * clipped off the right edge. The amount is what a phone asks for; the token split is a diagnostic
 * question, and it stays in the label for anyone reading with a screen reader.
 *
 * Its own class and not a rule on the pill's: two classes setting the same property tie, and the
 * winner is whichever Kobweb registers last.
 */
val UsageTokensStyle = CssStyle {
    base { Modifier }
    until(Breakpoints.Md) { Modifier.styleModifier { property("display", "none") } }
}

/** The dot on the status label. */
val PillDotStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "${Tokens.dot.value()}")
        property("height", "${Tokens.dot.value()}")
        property("border-radius", "50%")
        property("background", "currentColor")
        property("flex-shrink", "0")
    }
}

/** Separates the start of the session from what came after. */
val DayDividerStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .styleModifier {
            property("gap", "${Tokens.spaceMd.value()}")
            property("margin-bottom", "${Tokens.spaceXl.value()}")
            // Without this the label breaks when the rules on either side claim room.
            property("white-space", "nowrap")
            property("text-transform", "uppercase")
            property("letter-spacing", "0.08em")
        }
}

/** The rules on either side of the day divider. */
val DayDividerLineStyle = CssStyle.base {
    Modifier.styleModifier {
        property("flex", "1 1 auto")
        property("height", "${Tokens.hairline.value()}")
        property("background", Tokens.ornament.value())
    }
}

/** Who is speaking and how long it took. */
val AgentHeaderStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textSm.value())
        .styleModifier { property("margin-bottom", "${Tokens.spaceMd.value()}") }
}

/** The agent avatar. */
val AgentAvatarStyle = CssStyle.base {
    Modifier
        .backgroundColor(Tokens.accent.value())
        .color(Tokens.onAccent.value())
        .styleModifier {
            property("width", "${Tokens.avatar.value()}")
            property("height", "${Tokens.avatar.value()}")
            property("border-radius", "50%")
            property("display", "grid")
            property("place-items", "center")
            property("flex-shrink", "0")
        }
}

/** The agent name in the turn header. */
val AgentNameStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier { property("font-weight", "${Weight.Semibold}") }
}

/** The turn receipt: version, sections and weight, read from the HTML itself. */
val ArtifactCardStyle = CssStyle.base {
    Modifier
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
            // The conversation's one step below, not 16 above and below: the buttons after the
            // card were the only thing in the thread set 16 apart instead of 24.
            property("margin", "0 0 ${Tokens.spaceXl.value()}")
            property("overflow", "hidden")
        }
}

/** The receipt header. */
val ArtifactCardHeaderStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier {
            // Child of a Kobweb `Column`: without this it sizes to its content instead of the
            // card, and then the `Spacer` between the version and the size has nothing to grow
            // into. The two labels ended up touching, and "Version 1" beside "3 KB" read as
            // "Version 13 KB" - a size that looked wrong and a number that was not.
            property("width", "100%")
            property("box-sizing", "border-box")
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
            property("border-bottom", Tokens.glassDivider.value())
            property("text-transform", "uppercase")
            property("letter-spacing", "0.06em")
        }
}

/** A row of the receipt. */
val ArtifactCardRowStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textSm.value())
        .styleModifier { property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceLg.value()}") }
}

/** A quiet button: icon and text, because an icon alone forces guessing. */
val GhostButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceSm.value()}")
                property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceMd.value()}")
                property("background", "transparent")
                property("border", Tokens.glassEdge.value())
                // Not a primary touch target and it carries text: it does not need send's 44.
                property("min-height", "${Tokens.controlSm.value()}")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/**
 * The product mark inside whichever tile holds it.
 *
 * The size is a share of the tile and not a token of its own: the tile is already sized by one,
 * and a second number here would drift away from it the day that one moves. What is left over
 * is the margin that keeps the figure off the rounded corners.
 */
val MarkImageStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "block")
        property("width", "82%")
        property("height", "82%")
    }
}

/** The glyph of an icon-only button. */
val IconOnlyStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "${Tokens.iconMd.value()}")
        property("height", "${Tokens.iconMd.value()}")
        property("background-color", "currentColor")
        property("mask-repeat", "no-repeat")
        property("-webkit-mask-repeat", "no-repeat")
        property("mask-position", "center")
        property("-webkit-mask-position", "center")
        property("mask-size", "contain")
        property("-webkit-mask-size", "contain")
    }
}

/**
 * Attach photos: the same disc as send, on the other side of the field, and quiet until it is
 * wanted, because most messages carry none.
 */
val AttachButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.muted.value()).composerAction().styleModifier {
            property("background", "transparent")
            property("border", "none")
        }
    }
    cssRule("::before") { Modifier.composerReach() }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/** The photos waiting to go with the message, above the field. */
val AttachmentStripStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Tokens.spaceSm.value()}")
        property("margin-bottom", "${Tokens.spaceSm.value()}")
    }
}

/** One photo as a square thumbnail, cropped to fill it: it is a reminder, not a preview. */
val AttachmentThumbStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusSm.value()).styleModifier {
        property("position", "relative")
        property("width", "56px")
        property("height", "56px")
        property("flex-shrink", "0")
        property("border", "1px solid ${Tokens.lineStrong.value()}")
        property("background-size", "cover")
        property("background-position", "center")
    }
}

/** The cross that takes a photo back out, on its corner and big enough for a thumb. */
val AttachmentRemoveVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("position", "absolute")
            property("top", "-8px")
            property("right", "-8px")
            property("width", "24px")
            property("min-width", "24px")
            property("height", "24px")
            property("padding", "0")
            property("border-radius", "50%")
            property("display", "grid")
            property("place-items", "center")
            property("background", Tokens.glassRaisedFill.value())
            property("border", "1px solid ${Tokens.lineStrong.value()}")
            property("backdrop-filter", "blur(8px)")
        }
    }
}

/** Why a photo was not taken: said under the field, where the eye already is. */
val AttachmentErrorStyle = CssStyle.base {
    Modifier.color(Tokens.danger.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("margin-top", "${Tokens.spaceSm.value()}")
    }
}

/** A request's photos, above its bubble and on its side of the thread. */
val MessageImagesStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("justify-content", "flex-end")
        property("gap", "${Tokens.spaceSm.value()}")
        property("max-width", "85%")
        property("margin", "0 $TAIL_REACH ${Tokens.spaceSm.value()} auto")
    }
}

/** One of them: large enough to recognise the site, small enough not to take the thread over. */
val MessageImageStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusSm.value()).styleModifier {
        property("display", "block")
        property("width", "96px")
        property("height", "96px")
        property("object-fit", "cover")
        property("border", "1px solid ${Tokens.lineStrong.value()}")
    }
}

/** The shortcut hint, next to the field. */
val ComposerHintStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier {
            property("margin-top", "${Tokens.spaceSm.value()}")
            // A Kobweb `Row` is `nowrap`, so in a narrow chat column the second shortcut was
            // pushed out of the row and simply disappeared. Wrapping stacks the two instead,
            // and the row gap is smaller than the one between them so a wrapped pair still
            // reads as two lines of one thing.
            property("flex-wrap", "wrap")
            property("row-gap", "${Tokens.spaceXs.value()}")
        }
}

/** A key, inside the shortcut hint. */
/**
 * One shortcut: its key and what the key does, which must never be split between two lines.
 *
 * Without this the text wrapped inside the group and "to send" arrived under its own `Enter`,
 * which reads as two separate hints rather than one.
 */
val ShortcutStyle = CssStyle.base {
    Modifier.styleModifier { property("white-space", "nowrap") }
}

val KbdStyle = CssStyle.base {
    Modifier
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .fontSize(Tokens.textXs.value())
        .color(Tokens.muted.value())
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("padding", "1px ${Tokens.spaceSm.value()}")
            property("background", Tokens.glassRaisedFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/** The toolbar: what is shown, at what width, which version, and how to take it away. */
val ToolbarStyle = CssStyle {
    base {
        Modifier.styleModifier {
            // Child of a Kobweb Column: without this it sizes to its content, not the column.
            property("width", "100%")
            property("min-width", "0")
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
            property("padding-top", "calc(${Tokens.spaceMd.value()} + $SAFE_AREA_TOP)")
            property("border-bottom", Tokens.glassDivider.value())
            property("flex-shrink", "0")
            property("flex-wrap", "wrap")
        }
    }
    // Everything in here fits on one line in landscape and on none at all across a phone, so it
    // wrapped into rows nobody arranged. Two now, and they divide by what they are for: what to
    // look at, then what to take away. It took dropping the width rehearsal to get there - see
    // [ArtifactPanel] - because five controls come to 609px against the 316 a phone has, and
    // three rows of them were taking 163px of an 844px screen off the page they frame.
    //
    // The frame gives way as well: the outer padding and the gap between rows drop a step, which
    // is the difference between a band and a lid.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("padding", "${Tokens.spaceSm.value()}")
            property("padding-top", "calc(${Tokens.spaceSm.value()} + $SAFE_AREA_TOP)")
            property("row-gap", "${Tokens.spaceSm.value()}")
            property("column-gap", "${Tokens.spaceXs.value()}")
        }
    }
}

/** The two-option group: preview or code. */
val SegmentedStyle = CssStyle.base {
    Modifier
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("display", "inline-flex")
            property("padding", "${Tokens.spaceXxs.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/** One option of the segmented group. */
val SegmentVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceSm.value()}")
                property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceMd.value()}")
                property("background", "transparent")
                property("border", "none")
                property("min-height", "${Tokens.controlSm.value()}")
            }
    }
    hover { Modifier.color(Tokens.fg.value()) }
}

/** The chosen option. Background and color: tone alone is not enough to mark a state. */
val SegmentActiveStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("background", Tokens.glassRaisedFill.value())
        property("box-shadow", Tokens.glassRaisedShadow.value())
    }
}

/**
 * The browser frame around the preview. It says at a glance that what is inside is another
 * site, one that runs scripts, and not part of this one.
 */
val BrowserFrameStyle = CssStyle {
    base {
        Modifier.styleModifier {
            // Child of a Kobweb Column: without this it sizes to its content, not the column.
            property("width", "100%")
            property("min-width", "0")
            property("flex", "1 1 auto")
            property("min-height", "0")
            property("display", "flex")
            property("flex-direction", "column")
            property("padding", "${Tokens.spaceLg.value()}")
            property("overflow", "hidden")
            // What `cqw` in [PREVIEW_WIDTH] measures: this box does not animate.
            property("container-type", "inline-size")
        }
    }
    // On a phone the pane is the whole screen and this padding is the last thing between the
    // generated page and the edge. The window inside keeps its bar: the address is what says
    // the page comes from another origin, and that does not stop being true on a small screen.
    until(Breakpoints.Md) { Modifier.styleModifier { property("padding", "0") } }
}

/**
 * The width the preview is going to, set next to the window's `max-width` but not animated.
 *
 * The page inside jumps to it and the window's edge travels to meet it. When the iframe followed
 * the edge, the generated page redid its layout on every frame of the transition.
 */
const val PREVIEW_WIDTH = "--preview-width"

/**
 * The simulated browser window, whose `max-width` changes when switching to phone.
 *
 * Only this box moves: the iframe inside already has its final width ([PREVIEW_WIDTH]) and is
 * clipped while the edge catches up, and `contain` keeps the box's own relayout inside it.
 */
val BrowserWindowStyle = CssStyle {
    base {
        Modifier
            .borderRadius(Tokens.radiusLg.value())
            .styleModifier {
                property("flex", "1 1 auto")
                property("min-height", "0")
                property("display", "flex")
                property("flex-direction", "column")
                property("margin", "0 auto")
                property("width", "100%")
                property("background", Tokens.glassWellFill.value())
                property("border", Tokens.glassEdge.value())
                property("box-shadow", Tokens.glassPanelShadow.value())
                property("overflow", "hidden")

                property("transition", "max-width ${Motion.Slower} ${Motion.Curve}")
                property("contain", "layout paint")
            }
    }
    // Its frame gives up its padding on a phone, so a rounded corner here would be cut by the
    // screen's own edge.
    until(Breakpoints.Md) { Modifier.borderRadius(0.px) }
}

/** The top bar of the simulated browser. */
val BrowserBarStyle = CssStyle.base {
    Modifier.styleModifier {
        property("gap", "${Tokens.spaceMd.value()}")
        property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
        property("border-bottom", Tokens.glassDivider.value())
        property("flex-shrink", "0")
    }
}

/** The group of three traffic lights. */
val TrafficLightsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("gap", "6px")
        property("flex-shrink", "0")
    }
}

/** One traffic light, neutral: it is ornament, not state. */
val TrafficLightStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "${Tokens.dotOrnament.value()}")
        property("height", "${Tokens.dotOrnament.value()}")
        property("border-radius", "50%")
        property("background", Tokens.ornament.value())
    }
}

/**
 * The address bar. It shows the artifact's real URL: seeing that the origin is a different
 * one is what lets you check that the isolation still holds.
 */
val AddressStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("display", "block")
            property("flex", "1 1 auto")
            property("min-width", "0")
            property("padding", "${Tokens.spaceXs.value()} ${Tokens.spaceMd.value()}")
            property("background", Tokens.scrim.value())
            property("border", Tokens.glassDivider.value())
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
        }
}

/**
 * The sketch of the page under construction. Every block is a real section, read from the
 * `<!-- #id -->` markers in the HTML, so the progress is truthful.
 */
val SkeletonStyle = CssStyle.base {
    Modifier.styleModifier {
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
        property("flex", "1 1 auto")
        property("min-height", "0")
        property("overflow-y", "auto")
        property("padding", "${Tokens.spaceXl.value()}")
        // Confines the panel's blur recalculation to this box on every pulse of the bars.
        property("contain", "layout paint")
    }
}

/** A section of the sketch, with its name in the margin. */
val SkeletonSectionStyle = CssStyle.base {
    Modifier.styleModifier {
        property("align-items", "flex-start")
        property("gap", "${Tokens.spaceLg.value()}")
    }
}

/** The section name, in the margin of the sketch. */
val SkeletonLabelStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .styleModifier {
            property("width", "104px")
            property("flex-shrink", "0")
            property("padding-top", "${Tokens.spaceXxs.value()}")
        }
}

/** The bars of a section of the sketch. */
val SkeletonBodyStyle = CssStyle.base {
    Modifier.styleModifier {
        property("flex", "1 1 auto")
        property("min-width", "0")
    }
}

/** A finished bar. */
val SkeletonBarStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusSm.value()).styleModifier {
        property("height", "${Tokens.barLine.value()}")
        property("background", Tokens.skeletonDone.value())
    }
}

/**
 * A bar of the section being written. It pulses with `animation`, which the global stylesheet
 * neutralizes under `prefers-reduced-motion`; the state is also told apart by color.
 */
val SkeletonBarWritingStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusSm.value()).styleModifier {
        property("height", "${Tokens.barLine.value()}")
        property("background", Tokens.skeletonActive.value())
        property("animation", "ha-pulse ${Motion.PulseCycle} ${Motion.Breathe} infinite alternate")
    }
}

/** What has not arrived yet: hinted at, not asserted. */
val SkeletonGhostStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusSm.value()).styleModifier {
        property("height", "${Tokens.barLine.value()}")
        property("background", Tokens.skeletonGhost.value())
    }
}

/** The notice that more sections are coming, without pretending to know how many. */
val SkeletonTailStyle = CssStyle.base {
    Modifier
        .color(Tokens.muted.value())
        .fontSize(Tokens.textXs.value())
        .styleModifier { property("font-family", Tokens.fontMono.value()) }
        .styleModifier {
            property("display", "block")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("opacity", "0.7")
        }
}

/**
 * The entry screen: a narrow, centered column, light on purpose: there is no password, only an
 * address to send the access link to.
 *
 * It renders on its own, outside the shell, so it sets the viewport height itself. `dvh` on
 * top of `vh` because on phones the browser bar comes and goes and with `vh` the card drifts
 * off center.
 */
val GateStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .backgroundColor(Tokens.bg.value())
        .styleModifier {
            property("flex", "1 1 auto")
            property("min-height", "100vh")
            property("min-height", "100dvh")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", "${Tokens.spaceXl.value()}")
            // The same light as the workspace behind it, so the entrance is not a blank sheet.
            property("background-image", Tokens.backdrop.value())
            // The installed app runs full screen and reaches under the camera.
            property(
                "padding",
                "calc(${Tokens.spaceXl.value()} + env(safe-area-inset-top, 0px)) ${Tokens.spaceLg.value()} " +
                    "calc(${Tokens.spaceXl.value()} + env(safe-area-inset-bottom, 0px))",
            )
            property("text-align", "center")
        }
}

/**
 * The product above the entry card: the mark, the name and what it does.
 *
 * Without it the first screen of the installed app was a form with nothing saying whose it was.
 */
val GateBrandStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** Holds the tile and the light behind it; `isolate` keeps the light under the tile only. */
val GateMarkWrapStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "relative")
        property("isolation", "isolate")
        property("margin-bottom", "${Tokens.spaceSm.value()}")
    }
}

/**
 * The light behind the mark, the same accent disc the landing's hero has behind its mock-up.
 *
 * Still, unlike that one: it is the first thing on screen every time the app opens, and a
 * blurred layer that animates forever is the repaint cost the preview already showed.
 */
val GateGlowStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
        property("position", "absolute")
        property("inset", "-36px")
        property("z-index", "-1")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("filter", "blur(36px)")
        property("opacity", "0.35")
        property("pointer-events", "none")
    }
}

/** The mark's tile, at the proportions of the app icon: 18/64 of its side is the corner. */
val GateMarkTileStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.markTile.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "88px")
        property("height", "88px")
        property("border-radius", "28%")
        property("box-shadow", Tokens.glassPanelShadow.value())
    }
}

/**
 * The name, set like the wordmark in the landing's bar, at display size.
 *
 * Comfortaa's bold is its heaviest weight and still a thin stroke, so the presence comes from the
 * size: at `textXl` it read as light even though the face had loaded.
 */
val GateBrandNameStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textDisplay.value()).styleModifier {
        // Its own reset, not `PlainHeadingStyle`: two classes setting `font-size` tie, and that
        // one's `inherit` won and left the name at 16px.
        property("margin", "0")
        property("display", "inline-block")
        property("font-family", Tokens.fontDisplay.value())
        property("font-weight", "${Weight.Bold}")
        property("letter-spacing", Tracking.Tight)
        property("line-height", Leading.Tight)
        property("transform", "scaleX(1.1)")
    }
}

/** The promise, the landing hero's own two sentences. */
val GateBrandTaglineStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textBase.value()).styleModifier {
        property("display", "block")
        property("font-weight", "${Weight.Semibold}")
    }
}

/** The field's visible label, now that the card has no title above it. */
val GateLabelStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("display", "block")
        property("font-weight", "${Weight.Semibold}")
    }
}

/** "Why?": a link-shaped button inside the warning line. */
val GateWhyStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("display", "inline")
        property("padding", "0")
        property("border", "none")
        property("background", "none")
        property("font", "inherit")
        property("text-decoration", "underline")
        property("text-underline-offset", "3px")
        property("cursor", "pointer")
    }
}

/** The full reason, once asked for. */
val GateWhyDetailStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "block")
        property("margin-top", "${Tokens.spaceSm.value()}")
    }
}

/** The entry card. */
val GateCardStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusLg.value()).glassPanel().styleModifier {
        // The glass edge vanished against the light page; the card needs a line to stand on.
        property("border", "1px solid ${Tokens.line.value()}")
        property("width", "min(420px, 100%)")
        property("padding", "${Tokens.spaceXxl.value()}")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceLg.value()}")
        property("text-align", "left")
    }
}

/** The email field on the entry screen. */
val GateInputVariant = InputStyle.addVariant {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textBase.value())
            .borderRadius(Tokens.radiusMd.value())
            .glassWell()
            .styleModifier {
                property("width", "100%")
                property("box-sizing", "border-box")
                property("min-height", "${Tokens.controlLg.value()}")
                property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceLg.value()}")
                property("border", "1px solid ${Tokens.lineStrong.value()}")
            }
    }
}

/** The primary button on the entry screen. */
val GatePrimaryVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .backgroundColor(Tokens.accent.value())
            .color(Tokens.onAccent.value())
            .fontSize(Tokens.textSm.value())
            .borderRadius(Tokens.radiusMd.value())
            .styleModifier {
                property("width", "100%")
                property("min-height", "${Tokens.controlLg.value()}")
                property("margin-top", "${Tokens.spaceMd.value()}")
                property("border", "none")
                property("font-weight", "${Weight.Semibold}")
            }
    }
    hover { Modifier.styleModifier { property("filter", "brightness(1.08)") } }
}

/** The warning. It sits on the screen itself, not in a footnote nobody reads. */
val GateWarningStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("margin", "0")
        property("line-height", Leading.Normal)
        property("padding-left", "${Tokens.spaceMd.value()}")
        property("border-left", "2px solid ${Tokens.lineStrong.value()}")
    }
}

/**
 * A heading that takes its meaning from the tag and its look from where it sits.
 *
 * A browser gives every `h1`-`h6` a size and a margin of its own, which is wrong wherever the
 * heading is a label inside a row that already decided both. This resets them so the element can
 * be the right tag - which is what a screen reader navigates by - without becoming a different
 * shape on screen.
 */
val PlainHeadingStyle = CssStyle.base {
    Modifier.styleModifier {
        property("margin", "0")
        property("font-size", "inherit")
        property("font-weight", "inherit")
        property("letter-spacing", "inherit")
        property("color", "inherit")
    }
}


/**
 * The panel of past sessions that opens from the rail.
 *
 * It is always in the tree, so it has somewhere to transition from, and it lies over the
 * conversation instead of pushing it aside. When it pushed, its width animated and the chat and
 * the preview beside it were laid out and repainted on every frame: measured on a desktop, 7
 * frames lost out of 37. The negative margin keeps it after the rail without taking room, and
 * being constant, nothing is laid out while it moves.
 */
val HistoryPanelStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.surface.value())
            .styleModifier {
                property("position", "relative")
                property("z-index", "${Layer.Overlay}")
                property("width", "$HistoryWidth")
                property("margin-right", "-$HistoryWidth")
                property("flex-shrink", "0")
                property("display", "flex")
                property("flex-direction", "column")
                property("min-height", "0")
                property("border-right", Tokens.glassDivider.value())
                // Cast to the right only: to the left there is the rail, on the same sheet.
                property("box-shadow", "24px 0 40px -16px rgba(0, 0, 0, 0.3)")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                // Visibility flips at once on the way in and waits out the exit on the way out;
                // see [HistoryPanelClosedStyle].
                property(
                    "transition",
                    "transform ${Motion.Slow} ${Motion.Curve}, opacity ${Motion.Base} ${Motion.Ease}, visibility 0s",
                )
            }
    }
    // A phone is 396px wide and this panel is 260 of them: as a column it left the conversation
    // a gutter of 130 in which words broke mid-syllable. So it becomes a drawer over everything,
    // rail included - it carries its own close button, and covering the whole width is what
    // spares it having to know how wide the rail is.
    //
    // Out of the flow rather than cancelled with a negative margin: the margin would have to
    // follow the animated width, and a custom property does not interpolate, so the workspace
    // jumped 260px sideways the instant it opened and crawled back over the animation. Absolute,
    // the width animates alone and nothing else moves; the fade is the whole transition here.
    until(Breakpoints.Md) {
        Modifier
            .backgroundColor(Tokens.surface.value())
            .styleModifier {
                property("position", "absolute")
                // A drawer and not a page: 85% of the width, the dimmed app showing beside it.
                property("inset", "0 auto 0 0")
                property("width", "min(85%, 360px)")
                property("margin-right", "0")
                property("border-right", "none")
                property("box-shadow", "0 0 40px rgba(0, 0, 0, 0.3)")
                // It slides in from the edge it belongs to. Visibility flips at once on the way
                // in and waits out the slide on the way out; see [HistoryPanelClosedStyle].
                property("transition", "transform ${Motion.Slow} ${Motion.Curve}, visibility 0s")
            }
    }
}

/**
 * The closed panel: a short step back towards the rail and faded, not slid all the way out from
 * under it, since the rail is see-through. `visibility: hidden` waits for the fade, and is what
 * takes the content out of the tab order.
 */
val HistoryPanelClosedStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateX(calc(-1 * ${Tokens.spaceLg.value()}))")
            property("visibility", "hidden")
            property(
                "transition",
                "transform ${Motion.Slow} ${Motion.Curve}, opacity ${Motion.Base} ${Motion.Ease}, " +
                    "visibility 0s linear ${Motion.Slow}",
            )
        }
    }
    // On a phone it keeps its width and slides off instead: a drawer that shrinks to nothing
    // reads as collapsing, not as going back where it came from.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("width", "min(85%, 360px)")
            property("opacity", "1")
            property("transform", "translateX(-100%)")
            property("transition", "transform ${Motion.Slow} ${Motion.Curve}, visibility 0s linear ${Motion.Slow}")
        }
    }
}

/**
 * The content, at the width the panel has when open.
 *
 * Below [Breakpoints.Md] the panel is a full-width drawer that only fades, so the fixed width
 * would leave the list in its left 260px.
 */
val HistoryContentStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("width", "${HistoryWidth}")
            property("flex-shrink", "0")
            property("padding", "${Tokens.spaceMd.value()} ${Tokens.spaceSm.value()}")
        }
    }
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("width", "100%")
            // At least as tall as the drawer, so the account can sit at its foot.
            property("min-height", "100%")
            property("padding", "${Tokens.spaceMd.value()}")
            property("padding-top", "calc(${Tokens.spaceMd.value()} + $SAFE_AREA_TOP)")
            property("padding-bottom", "0")
        }
    }
}

/** The heading of the sessions panel, with its close button. */
val HistoryHeadingStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        // Child of a Kobweb Column: without this it sizes to its content, not the column.
        property("width", "100%")
        property("min-width", "0")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", "${Tokens.spaceSm.value()}")
        val sm = Tokens.spaceSm.value()
        property("padding", "$sm $sm $sm ${Tokens.spaceMd.value()}")
        property("text-transform", "uppercase")
        property("letter-spacing", "0.08em")
        property("flex-shrink", "0")
    }
}

/**
 * Collapse the panel from inside. The rail icon also closes it, but that requires knowing it
 * was that icon which opened it.
 */
val PanelCloseVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("width", "${Tokens.controlSm.value()}")
                property("height", "${Tokens.controlSm.value()}")
                property("display", "grid")
                property("place-items", "center")
                property("background", "transparent")
                property("border", "none")
                property("padding", "0")
                property("flex-shrink", "0")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/** One session in the list. */
val HistoryItemVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textSm.value())
            .borderRadius(Tokens.radiusMd.value())
            .styleModifier {
                property("display", "block")
                // The row is a `Box`, which Kobweb lays out as a grid: `flex` said nothing there
                // and the button stayed at the width of its own text, so the open session's
                // highlight covered 151px of a 244px row. Measured on both screens.
                property("width", "100%")
                // min-width: 0 allows shrinking below the content: a long title wraps instead of spilling.
                property("min-width", "0")
                property("text-align", "left")
                property("padding", "${Tokens.spaceMd.value()}")
                property("background", "transparent")
                property("border", "none")
                property("line-height", Leading.Snug)
                property("height", "auto")
                // Silk's button ships nowrap, which cut the title mid-word.
                property("white-space", "normal")
            }
    }
    hover { Modifier.styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/** The open session, within the list. */
val HistoryItemActiveStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("background", Tokens.glassRaisedFill.value())
            property("box-shadow", Tokens.glassRaisedShadow.value())
        }
    }
    // In the phone's drawer the open session carries an accent bar, the one mark that survives
    // a glance at a list over a dimmed screen.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("box-shadow", "inset 3px 0 0 ${Tokens.accent.value()}, ${Tokens.glassRaisedShadow.value()}")
        }
    }
}

/** The session name: its first request, in two lines at most. */
val HistoryItemTitleStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "100%")
        property("overflow-wrap", "anywhere")
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        property("overflow", "hidden")
    }
}

/** The date and count of a session, below its title. */
val HistoryItemMetaStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("margin-top", "${Tokens.spaceXs.value()}")
    }
}

/**
 * The row of a session, anchor for the trash button, which sits inside absolutely positioned
 * so it does not steal width from the titles for a button that is hardly ever used.
 *
 * Whether the trash shows is not decided in CSS: a descendant selector would depend on the
 * class name Silk generates, and Kobweb does not emit custom properties in a generated class.
 * The hover lives in Kotlin state and the focus in the `focusVisible` of
 * [HistoryDeleteVariant].
 */
val HistoryRowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "relative")
        property("width", "100%")
        property("min-width", "0")
    }
}

/**
 * The button to delete a session. It only appears on hover or on focus: a control that only
 * exists with the mouse leaves out whoever navigates with the tab key.
 */
val HistoryDeleteVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("position", "absolute")
                property("top", "6px")
                property("right", "6px")
                property("width", "${Tokens.controlSm.value()}")
                property("height", "${Tokens.controlSm.value()}")
                property("display", "grid")
                property("place-items", "center")
                // Solid: it sits over the title, and with no background neither one is legible.
                property("background", "${Tokens.raised.value()}")
                property("border", Tokens.glassEdge.value())
                property("padding", "0")
                property("transition", "opacity ${Motion.Fast} ${Motion.Ease}")
            }
    }
    hover { Modifier.color(Tokens.danger.value()) }
    // Whoever tabs to the trash button has to see it.
    focusVisible {
        Modifier.color(Tokens.danger.value()).styleModifier { property("opacity", "1") }
    }
}

/**
 * The delete confirmation, in the row itself. Not a browser `confirm()`: that one blocks the
 * whole page and would freeze the stream currently arriving.
 */
val HistoryConfirmStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textSm.value())
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("padding", "${Tokens.spaceMd.value()}")
            property("background", Tokens.dangerWash.value())
            property("border", "1px solid ${Tokens.dangerLine.value()}")
            property("width", "100%")
            property("min-width", "0")
        }
}

/** The name of what is about to be deleted, inside the confirmation. */
val HistoryConfirmTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        property("overflow", "hidden")
        property("overflow-wrap", "anywhere")
        property("line-height", Leading.Snug)
    }
}

/** The consequence, small and below the name. */
val HistoryConfirmDetailStyle = CssStyle.base {
    Modifier.color(Tokens.danger.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("margin-top", "${Tokens.spaceXs.value()}")
        property("line-height", Leading.Snug)
    }
}

/**
 * Confirm the deletion. Filled and not outlined: it is the action being confirmed and has to
 * be told apart from the cancel button without reading the labels.
 */
val HistoryConfirmButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .backgroundColor(Tokens.danger.value())
            .color(Tokens.bg.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("flex", "1 1 0")
                property("padding", "0 ${Tokens.spaceMd.value()}")
                property("border", "none")
                property("font-weight", "${Weight.Semibold}")
                // Same height as cancel, or the row ends up staggered.
                property("min-height", "${Tokens.controlSm.value()}")
                property("white-space", "nowrap")
            }
    }
    hover { Modifier.styleModifier { property("filter", "brightness(1.08)") } }
}

/** Cancel the deletion. */
val HistoryCancelButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("flex", "1 1 0")
                property("padding", "0 ${Tokens.spaceMd.value()}")
                property("background", "transparent")
                property("border", Tokens.glassEdge.value())
                property("min-height", "${Tokens.controlSm.value()}")
                property("white-space", "nowrap")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.glassRaisedFill.value()) } }
}

/**
 * The wrapper of a control with its own tooltip. `position: relative` anchors the bubble, and
 * `inline-flex` keeps a block `div` from stretching the button to its column's width.
 */
val TooltipHostStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "relative")
        property("display", "inline-flex")
        property("flex-shrink", "0")
    }
}

/**
 * The tooltip bubble.
 *
 * `pointer-events: none` keeps it from appearing under the cursor, eating the button's
 * `mouseleave` and flickering. Solid background and not glass, because it can land over the
 * preview, a page of any color. No entrance animation: what is seen many times a day is not
 * animated.
 */
val TooltipBubbleStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textXs.value())
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("position", "absolute")
            property("z-index", "${Layer.Popover}")
            property("padding", "6px 10px")
            property("white-space", "nowrap")
            property("pointer-events", "none")
            property("line-height", Leading.Tight)
            property("background", "${Tokens.raised.value()}")
            property("border", Tokens.glassEdge.value())
            property("box-shadow", Tokens.glassRaisedShadow.value())
        }
}

/** To the right: the left rail, where there is no room below. */
val TooltipRightStyle = CssStyle.base {
    Modifier.styleModifier {
        property("left", "calc(100% + 10px)")
        property("top", "50%")
        property("transform", "translateY(-50%)")
    }
}

/** Below and centered: the artifact toolbar. */
val TooltipBottomStyle = CssStyle.base {
    Modifier.styleModifier {
        property("top", "calc(100% + 8px)")
        property("left", "50%")
        property("transform", "translateX(-50%)")
    }
}

/**
 * Below and aligned to the right edge.
 *
 * For the last control of the toolbar: centered, the bubble would fall outside the panel and
 * the container's `overflow: hidden` would clip it.
 */
val TooltipBottomEndStyle = CssStyle.base {
    Modifier.styleModifier {
        property("top", "calc(100% + 8px)")
        property("right", "0")
    }
}

/** Above and centered: the actions under a message, the way a chat app labels them. */
val TooltipTopStyle = CssStyle.base {
    Modifier.styleModifier {
        property("bottom", "calc(100% + 8px)")
        property("left", "50%")
        property("transform", "translateX(-50%)")
    }
}

/** Above and aligned right: the send button, tucked against the bottom edge. */
val TooltipTopEndStyle = CssStyle.base {
    Modifier.styleModifier {
        property("bottom", "calc(100% + 8px)")
        property("right", "0")
    }
}

/**
 * The version tab strip. No `max-width` and no `overflow-x`: content hidden without notice is
 * content that does not exist. With many versions it is the group that collapses into a
 * dropdown, in `VersionPicker`.
 */
val VersionTabsStyle = CssStyle.base {
    Modifier
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("display", "inline-flex")
            property("padding", "${Tokens.spaceXxs.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/** The anchor of the version dropdown: the popup hangs from here. */
val VersionMenuHostStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "relative")
        property("display", "inline-flex")
    }
}

/**
 * The dropdown with every version. Aligned to the right edge because centered it would fall
 * outside the panel, and with `max-height` and its own scroll so it never outgrows the screen.
 */
val VersionMenuStyle = CssStyle.base {
    Modifier
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("position", "absolute")
            property("top", "calc(100% + 8px)")
            property("right", "0")
            property("z-index", "${Layer.Popover}")
            property("display", "grid")
            property("grid-template-columns", "repeat(3, minmax(44px, 1fr))")
            property("gap", "4px")
            property("padding", "${Tokens.spaceSm.value()}")
            property("max-height", "260px")
            property("overflow-y", "auto")
            property("background", "${Tokens.raised.value()}")
            property("border", Tokens.glassEdge.value())
            property("box-shadow", Tokens.glassRaisedShadow.value())
        }
}

/** Code inside an agent sentence: a section name, a CSS property. */
val InlineCodeStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textSm.value())
        .borderRadius(Tokens.radiusSm.value())
        .styleModifier {
            property("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
            property("padding", "1px 5px")
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/**
 * The identity block at the foot of the sessions panel.
 *
 * It sits with the sessions and not in a dialog of its own because what it explains is where
 * those sessions live: a guest's pages hang from a key held in this browser, and saying so
 * anywhere else would be saying it away from the thing it is about.
 */
val IdentityCardStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textXs.value())
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-top", "${Tokens.spaceLg.value()}")
            property("padding", "${Tokens.spaceMd.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/** The heading of that block, in the same register as the panel's own. */
val IdentityTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("font-family", Tokens.fontMono.value())
        property("text-transform", "uppercase")
        property("letter-spacing", "0.08em")
    }
}

/** What it explains, muted and one step down. */
val IdentityBodyStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("margin-top", "${Tokens.spaceXs.value()}")
        property("line-height", Leading.Snug)
    }
}

/** The email field, sized for a side panel rather than for a screen of its own. */
val IdentityInputVariant = InputStyle.addVariant {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .glassWell()
            .styleModifier {
                property("width", "100%")
                property("box-sizing", "border-box")
                property("min-height", "${Tokens.controlSm.value()}")
                property("padding", "0 ${Tokens.spaceSm.value()}")
                property("border", "1px solid ${Tokens.line.value()}")
            }
    }
}

/** Its buttons: full width, stacked, because the panel is too narrow for a row of two. */
val IdentityButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textXs.value())
            .borderRadius(Tokens.radiusSm.value())
            .styleModifier {
                property("width", "100%")
                property("padding", "0 ${Tokens.spaceSm.value()}")
                property("background", "transparent")
                property("border", Tokens.glassEdge.value())
                property("min-height", "${Tokens.controlSm.value()}")
            }
    }
}

/**
 * The way past the entrance without typing an address.
 *
 * Outlined and not filled: it is a real way in, not the one being recommended, and the filled
 * button next to it is what says which is which without reading either label.
 */
val GateGuestVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.muted.value())
            .fontSize(Tokens.textSm.value())
            .borderRadius(Tokens.radiusMd.value())
            .styleModifier {
                property("width", "100%")
                property("background", "transparent")
                property("border", Tokens.glassEdge.value())
                property("min-height", "${Tokens.controlMd.value()}")
            }
    }
}

/**
 * The entrance form. It only sets the width, and it has to: a `<form>` sizes to its content, so
 * without this the field and its button end up narrower than the button below them and the card
 * reads as two columns that do not line up.
 */
val GateFormStyle = CssStyle.base {
    Modifier.styleModifier {
        property("width", "100%")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/**
 * One of the link's screens - waiting, approving, done - as a column of blocks: the heading with
 * its text, the panel, the actions. The blocks breathe apart; what is inside each stays together.
 * It stretches its children, because the card's column aligns to the start and would shrink them.
 */
val LinkStepStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "stretch")
        property("gap", "${Tokens.spaceLg.value()}")
        property("width", "100%")
    }
}

/** The heading and the line under it, kept close: they are read as one thing. */
val LinkHeaderStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceXs.value()}")
    }
}

/** The step's heading: the one thing to read first, so it is the largest text in the card. */
val LinkTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textLg.value()).styleModifier {
        property("margin", "0")
        property("font-weight", "${Weight.Bold}")
        property("letter-spacing", Tracking.Tight)
        property("line-height", Leading.Tight)
    }
}

/** The line under the heading, a step quieter. */
val LinkLeadStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("margin", "0")
        property("line-height", Leading.Normal)
    }
}

/** The address inside that line: what the person checks, so it stands out of it. */
val LinkEmailStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("font-weight", "${Weight.Semibold}")
        property("overflow-wrap", "anywhere")
    }
}

/**
 * The panel around the waiting number: its instruction, the number and the waiting sign, centered
 * in a well of their own so the number is found at a glance, often from the other browser.
 */
val MatchPanelStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusMd.value()).glassWell().styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "${Tokens.spaceMd.value()}")
        property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceMd.value()}")
        property("border", "1px solid ${Tokens.line.value()}")
        property("text-align", "center")
    }
}

/** The instruction above the number, balanced so no word is left alone on the second line. */
val MatchHintStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("margin", "0")
        property("line-height", Leading.Normal)
        property("text-wrap", "balance")
    }
}

/**
 * A way back that is not a decision - "use another email" - as quiet text and not as a second
 * button competing with the real one under it.
 */
val LinkBackStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("align-self", "center")
        property("padding", "${Tokens.spaceXs.value()} ${Tokens.spaceSm.value()}")
        property("border", "none")
        property("background", "none")
        property("font", "inherit")
        property("font-size", "${Tokens.textSm.value()}")
        property("text-decoration", "underline")
        property("text-underline-offset", "3px")
        property("cursor", "pointer")
    }
}

/** The waiting number: the largest thing on the screen, in figures of equal width. */
val MatchNumberStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textDisplay.value()).styleModifier {
        property("font-family", Tokens.fontDisplay.value())
        property("font-weight", "${Weight.Bold}")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "0.12em")
        // The spacing trails the last figure too; this puts the pair back on center.
        property("margin-right", "-0.12em")
        property("line-height", "1")
    }
}

/** "Waiting…", with the dot that says it is alive. */
val WaitingStatusStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceXs.value()}")
    }
}

/**
 * The dot. It pulses with `animation`, which the global stylesheet neutralizes under
 * `prefers-reduced-motion`; the word next to it says the same without motion.
 */
val WaitingDotStyle = CssStyle.base {
    Modifier.borderRadius(Tokens.radiusFull.value()).styleModifier {
        property("width", "${Tokens.dot.value()}")
        property("height", "${Tokens.dot.value()}")
        property("background", "${Tokens.accent.value()}")
        property("animation", "ha-pulse ${Motion.PulseCycle} ${Motion.Breathe} infinite alternate")
    }
}

/** The three numbers to pick from on the approval page, across the whole card. */
val MatchChoicesStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, 1fr)")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** One of those numbers: large, so it is compared at a glance with the one in the other window. */
val MatchChoiceVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .color(Tokens.fg.value())
            .fontSize(Tokens.textXl.value())
            .borderRadius(Tokens.radiusMd.value())
            .glassWell()
            .styleModifier {
                property("width", "100%")
                property("min-height", "64px")
                property("border", "1px solid ${Tokens.lineStrong.value()}")
                property("font-family", Tokens.fontDisplay.value())
                property("font-weight", "${Weight.Bold}")
                property("font-variant-numeric", "tabular-nums")
            }
    }
    hover { Modifier.styleModifier { property("border-color", "${Tokens.accent.value()}") } }
}

/** The approved screen: centered, a short ending. */
val LinkDoneStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("text-align", "center")
        property("padding", "${Tokens.spaceMd.value()} 0")
    }
}

/** The check above it, in the accent, in a round well. */
val LinkDoneIconStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).borderRadius(Tokens.radiusFull.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "48px")
        property("height", "48px")
        property("background", "${Tokens.accentSurface.value()}")
        property("border", "1px solid ${Tokens.accentLine.value()}")
        property("margin-bottom", "${Tokens.spaceXs.value()}")
    }
}

/**
 * The note under the first page's receipt: what to do with what was just made.
 *
 * It lives in the conversation and not in the artifact panel because it is read once, at the
 * moment it answers a question the person is actually asking, and then scrolls away with the
 * turn it belongs to. A permanent panel would be shouting it forever.
 */
val WhatNowStyle = CssStyle.base {
    Modifier
        .color(Tokens.fg.value())
        .fontSize(Tokens.textXs.value())
        .borderRadius(Tokens.radiusMd.value())
        .styleModifier {
            property("width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "${Tokens.spaceXl.value()}")
            property("padding", "${Tokens.spaceMd.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border", Tokens.glassEdge.value())
        }
}

/** Its heading, in the same mono register the panels use for a label. */
val WhatNowTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("font-family", Tokens.fontMono.value())
        property("text-transform", "uppercase")
        property("letter-spacing", "0.08em")
        property("margin-bottom", "${Tokens.spaceSm.value()}")
    }
}

/** Each of the two lines: one about the file, one about the link. */
val WhatNowLineStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("display", "block")
        property("line-height", Leading.Snug)
        property("margin-top", "${Tokens.spaceXs.value()}")
    }
}
